import React from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Button,
  Typography,
} from '@mui/material';
import { FindOrganizationDialog } from 'organizations';
import axiosInstance from 'utils/axiosUtil';

const styles = (theme) => ({
  filterDropDown: {
    width: 200,
    marginRight: theme.spacing(),
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  button: {
    padding: 0,
    textTransform: 'none',
  },
  select: {
    width: '100%',
  },
});

/**
 * "By Organization" filter for the Market Data (/claims) tabs.
 *
 * Deliberately loads NOTHING on page load. An earlier version derived its option list
 * from the distinct organizations in each tab's own result set, which meant a heavy
 * grouped aggregate over the whole filtered claims set ran on every page load and every
 * criteria change -- slow enough that the page often failed to render at all.
 *
 * Instead the control defaults to All and stays inert until it is clicked, at which
 * point it opens the standard FindOrganizationDialog (the same searchable picker used
 * for organizations elsewhere in the app) in multi-select mode. Only on Save does the
 * page re-query, with the chosen ids passed as filter.organization_ids.
 *
 * `value` is an array of organization ids; an EMPTY array means All.
 */
class OrganizationSelect extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      dialogOpen: false,
      // The in-progress selection while the dialog is open. Only committed on Save.
      workingRows: [],
      // id -> name, purely for the control's label. Populated from the dialog, and
      // looked up once for ids restored from the URL or saved criteria.
      names: {},
    };

    // Ids we have already tried to look up, so a failed or empty lookup can't loop.
    this.resolveAttempted = new Set();
  }

  componentDidMount() {
    this.resolveNames();
  }

  componentDidUpdate(prevProps) {
    if (
      JSON.stringify(this.props.value) !== JSON.stringify(prevProps.value) ||
      this.props.clientId !== prevProps.clientId
    ) {
      this.resolveNames();
    }
  }

  getSelectedIds() {
    const { value } = this.props;
    if (!Array.isArray(value)) {
      const single = parseInt(value, 10);
      return Number.isInteger(single) && single > 0 ? [single] : [];
    }
    return value
      .map((v) => parseInt(v, 10))
      .filter((v) => Number.isInteger(v) && v > 0);
  }

  /**
   * Names for ids we didn't pick in this session (a shared URL, or the last-used
   * criteria restored on load). This is a primary-key lookup on a handful of ids, not
   * the aggregate the old version ran -- and it only fires when something is actually
   * selected, so the default All state still costs nothing.
   */
  async resolveNames() {
    const missing = this.getSelectedIds().filter(
      (id) => !this.state.names[id] && !this.resolveAttempted.has(id)
    );
    if (missing.length === 0) {
      return;
    }
    missing.forEach((id) => this.resolveAttempted.add(id));

    try {
      const response = await axiosInstance.get('/Organizations', {
        params: {
          opts: {
            where: { id: missing },
            limit: missing.length,
            order: [['name', 'ASC']],
          },
        },
      });
      const rows = response?.data?.rows ?? response?.data ?? [];
      if (!Array.isArray(rows) || rows.length === 0) {
        return;
      }
      this.setState((state) => {
        const names = { ...state.names };
        rows.forEach((row) => {
          if (row && row.id) {
            names[row.id] = row.name;
          }
        });
        return { names };
      });
    } catch (err) {
      // A missing label is cosmetic -- fall back to the count.
      console.error('Failed to load organization filter names', err);
    }
  }

  getLabel() {
    const ids = this.getSelectedIds();
    if (ids.length === 0) {
      return 'All';
    }
    if (ids.length === 1) {
      return this.state.names[ids[0]] ?? '1 organization';
    }
    return `${ids.length} organizations`;
  }

  render() {
    const { classes, clientId } = this.props;
    const { dialogOpen, workingRows } = this.state;

    return (
      <>
        <FormControl
          variant='outlined'
          className={classes.filterDropDown}
          key='filter-organization'
        >
          <InputLabel>By Organization</InputLabel>
          <Button
            onClick={this.onOpenDialog}
            className={classes.button}
            disableRipple
          >
            <Select
              value='custom'
              label='By Organization'
              open={false}
              className={classes.select}
              renderValue={() => (
                <Typography align='left' noWrap>
                  {this.getLabel()}
                </Typography>
              )}
            >
              <MenuItem value='custom' />
            </Select>
          </Button>
        </FormControl>
        {dialogOpen && (
          <FindOrganizationDialog
            open={dialogOpen}
            clientId={clientId}
            onSelect={this.onDialogSelect}
            onMultiSelect={this.onDialogSelect}
            onSaveMultiSelect={this.onDialogSave}
            selectedRows={workingRows}
          />
        )}
      </>
    );
  }

  onOpenDialog = () => {
    // Seed the dialog with the current selection. DynamicTable matches selected rows
    // by id, so a name-less stub is enough to show the right rows ticked.
    const { names } = this.state;
    this.setState({
      dialogOpen: true,
      workingRows: this.getSelectedIds().map((id) => ({
        id,
        name: names[id] ?? '',
      })),
    });
  };

  // FindOrganizationDialog calls this with the row array as the selection changes, and
  // with null when the dialog is cancelled.
  onDialogSelect = (rows) => {
    if (rows === null) {
      this.setState({ dialogOpen: false, workingRows: [] });
      return;
    }
    this.setState({ workingRows: [...rows] });
  };

  onDialogSave = () => {
    const rows = this.state.workingRows ?? [];
    const names = { ...this.state.names };
    rows.forEach((row) => {
      if (row && row.id && row.name) {
        names[row.id] = row.name;
      }
    });

    this.setState({ dialogOpen: false, workingRows: [], names });
    // Saving with nothing ticked is how you get back to All from inside the dialog.
    this.props.onChange(rows.map((row) => row.id));
  };
}

OrganizationSelect.propTypes = {
  classes: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.number]),
  clientId: PropTypes.number,
};

function mapStateToProps(state) {
  return {
    clientId: state.app && state.app.selectedClient,
  };
}

const connected = connect(mapStateToProps)(withStyles(styles)(OrganizationSelect));
export { connected as OrganizationSelect };
